import { Stack, Tabs, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import { useLookups } from "../../api/hooks";
import type { ModuleId } from "../../api/types";
import { EvalPage } from "./eval-page";
import { ImportsPage } from "./imports-page";
import { IngestionPage } from "./ingestion-page";
import { ModulesPage } from "./modules-page";
import { CompaniesPage, DocTypesPage } from "./reference-pages";
import { SourcesPage } from "./sources-page";
import { SynonymsPage } from "./synonyms-page";
import { UsersPage } from "./users-page";

const TABS = [
  "ingestion",
  "inbox",
  "imports",
  "users",
  "companies",
  "doc-types",
  "synonyms",
  "eval",
  "modules",
] as const;

/** Tabs that belong to a module and disappear with it. */
const TAB_MODULE: Partial<Record<(typeof TABS)[number], ModuleId>> = {
  inbox: "inbox",
  imports: "csv-import",
  eval: "eval",
};

export function AdminLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const lookups = useLookups();
  const tabs = TABS.filter((tab) => {
    const m = TAB_MODULE[tab];
    return !m || (lookups.data?.modules?.[m] ?? true);
  });
  const current = tabs.find((tab) => location.pathname.startsWith(`/admin/${tab}`)) ?? "ingestion";
  return (
    <Stack gap="md">
      <Title order={2}>{t("admin.title")}</Title>
      <Tabs value={current} onChange={(v) => v && navigate(`/admin/${v}`)} keepMounted={false}>
        <Tabs.List>
          {tabs.map((tab) => (
            <Tabs.Tab key={tab} value={tab}>
              {t(`admin.tabs.${tab}`)}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>
      <Routes>
        <Route index element={<Navigate to="ingestion" replace />} />
        <Route path="ingestion" element={<IngestionPage />} />
        <Route path="inbox" element={<SourcesPage />} />
        <Route path="imports" element={<ImportsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="companies" element={<CompaniesPage />} />
        <Route path="doc-types" element={<DocTypesPage />} />
        <Route path="synonyms" element={<SynonymsPage />} />
        <Route path="eval" element={<EvalPage />} />
        <Route path="modules" element={<ModulesPage />} />
      </Routes>
    </Stack>
  );
}
